import {isFeatureEnabled} from '@github-ui/feature-flags'
import {HeaderAgentTasks, type HeaderAgentTasksFragment} from '@github-ui/issue-agent-tasks/HeaderAgentTasks'
import {useIsVisualizationEnabled} from '@github-ui/issue-agent-tasks/utils/is-visualization-enabled'
import {clsx} from 'clsx'
import {Suspense} from 'react'
import {graphql, useFragment} from 'react-relay'

import {TEST_IDS} from '../../constants/test-ids'
import {ContentWrapper} from '../ContentWrapper'
import type {OptionConfig} from '../OptionConfig'
import type {HeaderMetadata$key} from './__generated__/HeaderMetadata.graphql'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import {HeaderIssueType, type HeaderIssueTypeFragment, SmallHeaderIssueType} from './HeaderIssueType'
import {type HeaderMenuBaseProps, StickyHeaderMenu} from './HeaderMenu'
import styles from './HeaderMetadata.module.css'
import {type HeaderParentFragment, HeaderParentTitle} from './HeaderParentTitle'
import {HeaderState, type HeaderStateFragment} from './HeaderState'
import {HeaderSubIssueSummary, type HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import {IssueMetadata, type IssueMetadataFragment} from './IssueMetadata'
import {type LinkedPullRequestsFragment, LinkedPullRequestsInternal} from './LinkedPullRequests'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import {LinkedSecurityAlerts} from './LinkedSecurityAlerts'
import {RepositoryPill, SmallRepositoryPill} from './RepositoryPill'
import {StickyHeaderTitle} from './StickyHeaderTitle'
import {SmallTrackedBy, TrackedBy, type TrackedByFragment} from './TrackedBy'

export type HeaderMetadataProps = {
  headerMetadataKey: HeaderMetadata$key
  optionConfig: OptionConfig
  isSticky: boolean
  parent?: HeaderParentFragment | null
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
} & HeaderMenuBaseProps

export function HeaderMetadata({
  headerMetadataKey,
  optionConfig,
  isSticky,
  parent,
  blockedBySecondaryData,
  trackedBySecondaryData,
  subIssueSecondaryData,
  linkedSecurityAlertsData,
}: HeaderMetadataProps) {
  const showAgentTasksPanel = useIsVisualizationEnabled()
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  const {showRepositoryPill} = optionConfig

  const headerData = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment HeaderMetadata on Issue {
        titleHTML
        url
        number
        viewerCanUpdateNext
        repository {
          nameWithOwner
          name
          owner {
            login
          }
          isArchived
          isPrivate
          isWritable
        }
        issueType {
          name
          color
        }
        subIssuesSummary {
          total
          completed
        }
        state
        stateReason
        duplicateOf {
          number
          url
          repository {
            name
            owner {
              login
            }
          }
        }
        linkedPullRequests: closedByPullRequestsReferences(first: 10, includeClosedPrs: false, orderByState: true) {
          nodes {
            repository {
              name
              nameWithOwner
              owner {
                login
              }
            }
            url
            number
            state
            isDraft
          }
        }
        agentAssignments {
          __typename
        }
        closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
          nodes {
            id
            number
            url
            state
            databaseId
          }
        }
        labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              color
              name
              nameHTML
              description
              url
            }
          }
        }
        milestone {
          id
          title
          closed
          dueOn
          progressPercentage
          url
          closedAt
        }
        assignedActors(first: 20) {
          nodes {
            ... on Actor {
              __typename
              id
              login
              name
              profileResourcePath
              avatarUrl(size: 64)
              ... on Bot {
                isCopilot
                isAgent
                displayName
              }
            }
          }
        }
      }
    `,
    headerMetadataKey,
  )

  const classNames = []
  if (optionConfig.insideSidePanel) {
    classNames.push('js-second-sticky-header')
  }
  return (
    <>
      {/* Regular header */}
      <ContentWrapper>
        <div
          data-testid={TEST_IDS.issueMetadataFixed}
          className={clsx(
            styles.metadataContainer,
            stickyHeaderEnabled && styles.metadataContainer_stickyHeader,
            !stickyHeaderEnabled && isSticky && styles.metadataContainerSticky,
          )}
        >
          <div
            className={clsx(
              styles.metadataBorder,
              stickyHeaderEnabled && styles.metadataBorder_stickyHeader,
              isFeatureEnabled('issue_inline_avatars') && styles.metadataBorder_inlineAvatars,
            )}
          >
            <div className={styles.metadataContent}>
              {/* RELAY-CLEANUP(HeaderMetadata): boundary cast; remove when HeaderMetadata leaves Relay. */}
              <HeaderState isSticky={isSticky} stateData={headerData as HeaderStateFragment} />
              {/* RELAY-CLEANUP(HeaderMetadata): open-enum `color` from the still-Relay parent.
                  Remove when HeaderMetadata leaves Relay. */}
              <HeaderIssueType data={headerData as HeaderIssueTypeFragment} />
              {/* RELAY-CLEANUP(HeaderMetadata): open-enum `state` from the still-Relay parent.
                  Remove when HeaderMetadata leaves Relay. */}
              {showAgentTasksPanel && <HeaderAgentTasks issueData={headerData as HeaderAgentTasksFragment} />}

              <HeaderSubIssueSummary subIssuePrimaryKey={headerData} subIssueSecondaryKey={subIssueSecondaryData} />

              <HeaderBlockedBySummary blockedBySecondaryData={blockedBySecondaryData} />

              <HeaderParentTitle
                parent={parent}
                optionConfig={optionConfig}
                repositoryNameWithOwner={headerData.repository.nameWithOwner}
              />

              <LinkedSecurityAlerts issueKey={linkedSecurityAlertsData} />

              <Suspense>
                <TrackedBy url={headerData.url} trackedByData={trackedBySecondaryData} />
              </Suspense>

              {/* RELAY-CLEANUP(HeaderMetadata): open-enum `state` in linkedPullRequests from still-Relay parent.
                  Remove when HeaderMetadata leaves Relay. */}
              <LinkedPullRequestsInternal issueData={headerData as unknown as LinkedPullRequestsFragment} />
              {showRepositoryPill && <RepositoryPill repositoryPillData={headerData} />}
            </div>
          </div>
        </div>
      </ContentWrapper>
      {/* "Sticky" header */}
      {!stickyHeaderEnabled && (
        <div
          data-testid={TEST_IDS.issueMetadataSticky}
          id="issue-viewer-sticky-header"
          className={clsx(
            classNames,
            styles.stickyContainer,
            isSticky ? styles.stickyContainerVisible : styles.stickyContainerHidden,
          )}
        >
          <ContentWrapper>
            <div className={styles.stickyContent}>
              {/* RELAY-CLEANUP(HeaderMetadata): boundary cast; remove when HeaderMetadata leaves Relay. */}
              <HeaderState isSticky={isSticky} stateData={headerData as HeaderStateFragment} />
              <div className={styles.titleAndMetadata}>
                <StickyHeaderTitle headerTitleData={headerData} scrollToTopOnClick={optionConfig.scrollToTopOnClick} />
                <div
                  className={clsx(
                    styles.smallMetadataRow,
                    optionConfig.useViewportQueries
                      ? styles.smallMetadataRow_viewport
                      : styles.smallMetadataRow_container,
                  )}
                >
                  {/* RELAY-CLEANUP(HeaderMetadata): open-enum `color` from the still-Relay parent.
                      Remove when HeaderMetadata leaves Relay. */}
                  <SmallHeaderIssueType data={headerData as HeaderIssueTypeFragment} />
                  {/* RELAY-CLEANUP(HeaderMetadata): open-enum `state` from the still-Relay parent.
                      Remove when HeaderMetadata leaves Relay. */}
                  {showAgentTasksPanel && (
                    <HeaderAgentTasks issueData={headerData as HeaderAgentTasksFragment} isSticky />
                  )}
                  <HeaderSubIssueSummary
                    subIssuePrimaryKey={headerData}
                    subIssueSecondaryKey={subIssueSecondaryData}
                    size="small"
                  />
                  <HeaderBlockedBySummary blockedBySecondaryData={blockedBySecondaryData} size="small" />
                  <HeaderParentTitle
                    parent={parent}
                    optionConfig={optionConfig}
                    repositoryNameWithOwner={headerData.repository.nameWithOwner}
                    small
                  />
                  <LinkedSecurityAlerts issueKey={linkedSecurityAlertsData} isSmall />
                  <Suspense>
                    <SmallTrackedBy url={headerData.url} trackedByData={trackedBySecondaryData} />
                  </Suspense>
                  {/* RELAY-CLEANUP(HeaderMetadata): open-enum `state` in linkedPullRequests from still-Relay parent.
                      Remove when HeaderMetadata leaves Relay. */}
                  <LinkedPullRequestsInternal issueData={headerData as unknown as LinkedPullRequestsFragment} isSmall />
                  {showRepositoryPill && <SmallRepositoryPill repositoryPillData={headerData} />}
                </div>
              </div>
              <div>
                {/* RELAY-CLEANUP(HeaderMetadata): plain props from the still-Relay parent fragment.
                    Remove when HeaderMetadata leaves Relay. */}
                <StickyHeaderMenu optionConfig={optionConfig} headerMenuData={headerData} />
              </div>
            </div>
          </ContentWrapper>
        </div>
      )}
      <ContentWrapper>
        {/* RELAY-CLEANUP(HeaderMetadata): still-Relay parent data uses the masked Actor interface shape.
            Remove when HeaderMetadata leaves Relay. */}
        <IssueMetadata metadata={headerData as IssueMetadataFragment} />
      </ContentWrapper>
    </>
  )
}
